import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'

import { useAuthStore } from '../../store/auth'
import { useOnClickOutside } from '../../lib/OnClickOutside'
import { isValidJsonString } from '../../lib/uiHelper'
import defaultAvatar from '../../assets/images/default-avatar.png'

/**
 * AppLayoutHeader Component
 *
 * This component renders the header of the application layout. It includes the
 * logo, navigation buttons, user roles dropdown, dark/light mode toggle, and
 * user profile options. The header is responsive and adapts to different screen
 * sizes.
 *
 * @module appLayout/AppLayoutHeader
 * @description This component renders the header of the application layout. It includes the
 * logo, navigation buttons, user roles dropdown, dark/light mode toggle, and
 * user profile options. The header is responsive and adapts to different screen
 * sizes.
 * @example
 * // Usage within a layout component
 * <AppLayoutHeader
 *   envData={envData}
 *   authDetails={authDetails}
 *   setToggleMenu={setToggleMenu}
 *   toggleMenu={toggleMenu}
 * />
 *
 * @param {Object} envData - The environment data containing theme configurations.
 * @param {Object} authDetails - The authentication details for the current user.
 * @param {Function} setToggleMenu - Function to toggle the sidebar menu.
 * @param {boolean} toggleMenu - Boolean indicating if the sidebar menu is toggled.
 *
 * @returns {React.ReactElement} The rendered header component.
 */
let deferredPrompt
export const AppLayoutHeader = ({
  envData,
  authDetails,
  setToggleMenu,
  toggleMenu,
  modSchema,
  activeOrgRoutes,
  isDefaultOrg,
  removeDefaultApp,
  setApp,
}) => {
  const [installable, setInstallable] = React.useState(false)
  const isLoggedIn = isValidJsonString(authDetails)
    ? authDetails.loggedIn
    : JSON.parse(JSON.stringify(authDetails)).loggedIn || false
  const usersRoles = authDetails.session?.user?.userRoles
  const [userDropDown, setUserDropDown] = React.useState(false)
  const userDropDownRef = React.useRef()
  useOnClickOutside(userDropDownRef, () => setUserDropDown(false))
  const [userRoles, setUserRoles] = React.useState(false)
  const userRolesRef = React.useRef()
  useOnClickOutside(userRolesRef, () => setUserRoles(false))

  const [favOrgs, setFavOrgs] = React.useState(false)
  const favOrgsRef = React.useRef()
  useOnClickOutside(favOrgsRef, () => setFavOrgs(false))

  const layoutElem = document.getElementsByTagName('html')
  const [hamburger, setHamburger] = React.useState(false)

  const defaultThemeLayout = sessionStorage.getItem('themeLayout')

  const [isDark, setIsDark] = React.useState(defaultThemeLayout || false)

  const themeLayout = (layout) => {
    //setAttribute("data-layout", layout);
    const htmElem = document.getElementsByTagName('html')
    if (layout === true) {
      sessionStorage.setItem('themeLayout', layout)
      htmElem[0].setAttribute('data-sidebar', 'dark')
      htmElem[0].setAttribute('data-bs-theme', 'dark')
    } else {
      sessionStorage.removeItem('themeLayout')
      htmElem[0].setAttribute('data-sidebar', 'light')
      htmElem[0].setAttribute('data-bs-theme', 'light')
    }
    setIsDark(layout)
  }

  React.useEffect(() => {
    window.addEventListener('beforeinstallprompt', (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault()
      // Stash the event so it can be triggered later.
      deferredPrompt = e
      // Update UI notify the user they can install the PWA
      setInstallable(true)
    })

    window.addEventListener('appinstalled', () => {
      // Log install to analytics
      console.log('INSTALL: Success')
    })
  }, [])

  const handleInstallClick = () => {
    // Hide the app provided install promotion
    setInstallable(false)
    // Show the install prompt
    deferredPrompt.prompt()
    // Wait for the user to respond to the prompt
    deferredPrompt.userChoice
      .then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('User accepted the install prompt')
        } else {
          console.log('User dismissed the install prompt')
        }
      })
      .catch((err) => console.log(err))
  }

  React.useEffect(() => {
    if (window.innerWidth < 768) {
      const logoId = document.getElementById('brandLogo')
      logoId.style.display = 'block'
    }

    if (sessionStorage.getItem('themeLayout') && sessionStorage.getItem('themeLayout') === 'true') {
      const htmElem = document.getElementsByTagName('html')
      htmElem[0].setAttribute('data-sidebar', 'dark')
      htmElem[0].setAttribute('data-bs-theme', 'dark')
    }
  }, [])
  if (window.attachEvent) {
    window.attachEvent('onresize', function () {
      if (window.innerWidth < 768) {
        const logoId = document.getElementById('brandLogo')
        logoId.style.display = 'block'
      } else {
        const logoId = document.getElementById('brandLogo')
        logoId.style.display = 'none'
      }
    })
  } else if (window.addEventListener) {
    window.addEventListener(
      'resize',
      function () {
        if (window.innerWidth < 768) {
          const logoId = document.getElementById('brandLogo')
          logoId.style.display = 'block'
        } else {
          const logoId = document.getElementById('brandLogo')
          logoId.style.display = 'none'
        }
      },
      true,
    )
  }

  const minSideMenu = () => {
    if (window.innerWidth < 768) {
      document.body.classList.add('vertical-sidebar-enable')
      document.body.classList.remove('twocolumn-panel')
      setToggleMenu(!toggleMenu)
    } else {
      if (!hamburger) {
        layoutElem[0].setAttribute('data-sidebar-size', 'sm')
      } else {
        layoutElem[0].setAttribute('data-sidebar-size', 'lg')
      }
    }
    setHamburger(!hamburger)
  }

  const { setRole, setOrgRole } = useAuthStore()
  const changeRole = (roleName, roleId) => {
    setRole(roleId, roleName)
    window.location.href = '/console/dashboard'
  }

  const changeOrgRole = (roleName, roleId) => {
    setOrgRole(roleId, roleName)
    window.location.href = '/organization/dashboard'
  }

  const avatarRef = useRef()
  const onAvatarImageError = () => (avatarRef.current.src = defaultAvatar)

  const getWebView = () => {
    let isWebview = false

    const versionName = window.versionWebView?.getVersionName()
    if (versionName !== null && versionName !== undefined) {
      isWebview = true
    }
    return isWebview
  }

  return (
    <>
      <header id={modSchema.noLayout ? '' : 'page-topbar'}>
        <div className="trtui-layout-width">
          <div className="trtui-navbar-header">
            <div className="trtui-d-flex">
              {/* LOGO */}
              <div className="trtui-navbar-brand-box trtui-horizontal-logo" id="brandLogo">
                <Link to="/" className="trtui-logo trtui-logo-dark">
                  <span className="trtui-logo-sm">
                    <img
                      src={envData.REACT_APP_THEME_FAVICON}
                      style={{ maxWidth: '36px', maxHeight: '36px' }}
                      alt="logo-sm-dark"
                    />
                  </span>
                  <span className="trtui-logo-lg">
                    <img src={envData.REACT_APP_THEME_LOGO} alt="logo-lg-dark" />
                  </span>
                </Link>
                <Link to="/" className="trtui-logo trtui-logo-light">
                  <span className="trtui-logo-sm">
                    <img
                      src={envData.REACT_APP_THEME_FAVICON}
                      style={{ maxWidth: '36px', maxHeight: '36px' }}
                      alt="logo-sm-light"
                    />
                  </span>
                  <span className="trtui-logo-lg">
                    <img src={envData.REACT_APP_THEME_LOGO} alt="logo-lg-light" />
                  </span>
                </Link>
              </div>
              {!modSchema.noLayout && (
                <button
                  type="button"
                  className="trtui-btn trtui-btn-sm trtui-px-3 trtui-fs-16 trtui-header-item trtui-vertical-menu-btn trtui-topnav-hamburger trtui-d-block trtui-d-md-none"
                  onClick={minSideMenu}
                  data-testid="alhToggleSidebar"
                >
                  <span className={`trtui-hamburger-icon`}>
                    <span />
                    <span />
                    <span />
                  </span>
                </button>
              )}
            </div>
            <div className="trtui-d-flex trtui-align-items-center">
              {/* {activeOrg !== null && (
                <>
                  <div className="trtui-btn-group trtui-d-block trtui-d-md-none">
                    <div className="trtui-dropdown trtui-topbar-head-dropdown trtui-ms-1 trtui-header-item">
                      <button
                        type="button"
                        className="trtui-btn trtui-btn-icon trtui-btn-topbar trtui-btn-ghost-secondary trtui-rounded-circle show"
                        data-bs-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="true"
                        onClick={() => setOrgDropDown(!orgDropDown)}
                        title="Selected Organization"
                      >
                        <i className="bx bx-category-alt trtui-text-primary trtui-fs-22"></i>
                      </button>
                      <div
                        className={`trtui-dropdown-menu trtui-dropdown-menu-lg trtui-p-0 trtui-dropdown-menu-end ${orgDropDown ? 'trtui-show' : ''}`}
                        data-popper-placement="bottom-end"
                        style={{
                          position: 'absolute',
                          inset: '0px 0px auto auto',
                          margin: '0px',
                          transform: 'translate3d(0px, 58.4px, 0px)',
                        }}
                        ref={orgDropDownRef}
                      >
                        <div className="trtui-p-3 trtui-border-top-0 trtui-border-start-0 trtui-border-end-0 trtui-border-dashed trtui-border">
                          <div className="trtui-row trtui-align-items-center">
                            <div className="trtui-col">
                              <h4 className="trtui-m-0 trtui-fw-semibold trtui-fs-15">
                                {' '}
                                {activeOrg}{' '}
                              </h4>
                            </div>
                          </div>
                        </div>

                        <div className="trtui-p-2">
                          <div className="trtui-row trtui-g-0">
                            <div className="trtui-col">
                              <a className="trtui-dropdown-icon-item" onClick={unsetOrg} href="#!">
                                <span>Exit Organization</span>
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )} */}
              {envData.REACT_APP_APP_ISONLINE === '1' &&
                isDefaultOrg?.favouriteOrgs?.length > 0 && (
                  <div className="trtui-dropdown trtui-topbar-head-dropdown trtui-ms-1 trtui-header-item">
                    <button
                      type="button"
                      className="trtui-btn trtui-btn-icon trtui-btn-topbar trtui-btn-ghost-secondary trtui-rounded-circle"
                      id="page-header-cart-dropdown"
                      data-bs-toggle="dropdown"
                      data-bs-auto-close="outside"
                      aria-haspopup="true"
                      aria-expanded="false"
                      title="Favorite Organizations"
                      onClick={() => setFavOrgs(!favOrgs)}
                    >
                      <i className="ri-apps-2-line trtui-fs-22" />
                      <span className="trtui-position-absolute trtui-topbar-badge trtui-cartitem-badge trtui-fs-10 trtui-translate-middle trtui-badge trtui-rounded-pill trtui-bg-info">
                        {isDefaultOrg.favouriteOrgs?.length}
                      </span>
                    </button>
                    <div
                      className={`trtui-dropdown-menu trtui-dropdown-menu-xl trtui-dropdown-menu-end trtui-p-0 trtui-dropdown-menu-cart ${favOrgs ? 'trtui-show' : ''}`}
                      aria-labelledby="page-header-cart-dropdown"
                      style={{
                        position: 'absolute',
                        inset: '0px 0px auto auto',
                        margin: '0px',
                        WebkitTransform: 'translate(0px, 58px)',
                        msTransform: 'translate(0px, 58px)',
                        transform: 'translate(0px, 58px)',
                      }}
                      ref={favOrgsRef}
                    >
                      <div className="trtui-p-3 trtui-border-top-0 trtui-border-start-0 trtui-border-end-0 trtui-border-dashed trtui-border">
                        <div className="trtui-row trtui-align-items-center">
                          <div className="trtui-col">
                            <h6 className="trtui-m-0 trtui-fs-16 trtui-fw-semibold">
                              {' '}
                              Favorite Organizations
                            </h6>
                          </div>
                          <div className="trtui-col-auto">
                            <span className="trtui-badge trtui-bg-warning-subtle trtui-text-warning trtui-fs-13">
                              <span className="trtui-cartitem-badge">
                                {isDefaultOrg.favouriteOrgs.length}
                              </span>{' '}
                              Orgs
                            </span>
                          </div>
                        </div>
                        <div className="trtui-p-2">
                          {isDefaultOrg.favouriteOrgs?.map((org, ind) => (
                            <div
                              key={ind}
                              className="trtui-d-block trtui-dropdown-item trtui-dropdown-item-cart trtui-text-wrap trtui-px-3 trtui-py-2"
                            >
                              <div className="trtui-d-flex trtui-align-items-center">
                                <div className="trtui-flex-grow-1">
                                  <h6 className="trtui-mt-0 trtui-mb-1 trtui-fs-14">
                                    <span
                                      className="trtui-text-reset trtui-cursor-pointer"
                                      onClick={() => setApp(org)}
                                    >
                                      {org.orgName}
                                    </span>
                                  </h6>
                                  <p className="trtui-mb-0 trtui-fs-12 trtui-text-muted">
                                    {org.orgCity}, <span>{org.orgProvince}</span>
                                  </p>
                                </div>
                                <div className="trtui-ps-2">
                                  <button
                                    type="button"
                                    className="trtui-btn trtui-btn-icon trtui-btn-sm trtui-btn-ghost-danger trtui-remove-item-btn"
                                    onClick={() => removeDefaultApp(org)}
                                    title="Remove from favorites"
                                  >
                                    <i className="ri-close-fill trtui-fs-16" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              {!getWebView() && !modSchema.noLayout && (
                <div className="trtui-ms-1 trtui-header-item trtui-d-flex">
                  <a
                    href={envData.REACT_APP_WEBSITE}
                    rel="noopener noreferrer"
                    className="trtui-btn trtui-btn-icon trtui-btn-topbar trtui-btn-ghost-secondary trtui-rounded-circle"
                  >
                    <i className={`bx bx-home trtui-fs-22`} />
                  </a>
                </div>
              )}
              {!modSchema.noLayout &&
                isLoggedIn &&
                envData.REACT_APP_TYPE === 'app' &&
                usersRoles?.length &&
                usersRoles.length > 0 && (
                  <div className="trtui-dropdown trtui-topbar-head-dropdown trtui-ms-1 trtui-header-item">
                    <button
                      type="button"
                      className={`trtui-btn trtui-btn-icon trtui-btn-topbar trtui-btn-ghost-secondary trtui-rounded-circle ${
                        userRoles && 'trtui-show'
                      }`}
                      data-bs-toggle="dropdown"
                      aria-haspopup="true"
                      aria-expanded="false"
                      onClick={() => setUserRoles(!userRoles)}
                    >
                      <i className="bx bx-user-pin trtui-fs-22" />
                    </button>
                    <div
                      className={`trtui-dropdown-menu trtui-dropdown-menu-lg trtui-p-0 trtui-dropdown-menu-end ${
                        userRoles && 'trtui-show'
                      }`}
                      style={{
                        position: 'absolute',
                        inset: '0px 0px auto auto',
                        margin: '0px',
                        WebkitTransform: 'translate(0px, 58px)',
                        msTransform: 'translate(0px, 58px)',
                        transform: 'translate(0px, 58px)',
                      }}
                      ref={userRolesRef}
                    >
                      <div className="trtui-p-3 trtui-border-top-0 trtui-border-start-0 trtui-border-end-0 trtui-border-dashed trtui-border">
                        <div className="trtui-row trtui-align-items-center">
                          <div className="trtui-col">
                            <h6 className="trtui-m-0 trtui-fw-semibold trtui-fs-15">User Roles</h6>
                          </div>
                        </div>
                      </div>
                      <div className="trtui-p-2">
                        <div className="trtui-row trtui-g-0">
                          {envData.REACT_APP_APP_ISONLINE === '1' &&
                            usersRoles.map(
                              (role, roleIndex) =>
                                role.roleName !== 'AppAdmin' && (
                                  <div
                                    key={roleIndex}
                                    className="trtui-col-4"
                                    style={{
                                      '--vz-bg-opacity': 1,
                                      backgroundColor:
                                        authDetails.role === role.roleName
                                          ? 'rgba(var(--vz-light-rgb),var(--vz-bg-opacity))'
                                          : '',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                      changeRole(role.roleId, role.roleName)
                                    }}
                                  >
                                    <span className="trtui-dropdown-icon-item" href="#!">
                                      <img src={defaultAvatar} alt="bitbucket" />
                                      <span>{role.roleName}</span>
                                    </span>
                                  </div>
                                ),
                            )}
                          {activeOrgRoutes !== null && (
                            <>
                              {envData.REACT_APP_APP_ISONLINE === '1' && (
                                <div className="trtui-border-bottom trtui-border-dark trtui-w-100 trtui-mt-1 trtui-mb-1"></div>
                              )}
                              {activeOrgRoutes.formData?.orgRoles?.length > 0 &&
                                activeOrgRoutes.formData?.orgRoles.map((om, omInd) => (
                                  <div
                                    key={omInd}
                                    className="trtui-col-4"
                                    style={{
                                      '--vz-bg-opacity': 1,
                                      backgroundColor:
                                        authDetails.orgRole === om.rowFormData.roleName
                                          ? 'rgba(var(--vz-light-rgb),var(--vz-bg-opacity))'
                                          : '',
                                      cursor: 'pointer',
                                      borderTop: '1px solid rgba(255,255,255,0.3)',
                                    }}
                                    onClick={() => {
                                      changeOrgRole(om.rowFormData.roleId, om.rowFormData.roleName)
                                    }}
                                  >
                                    <span className="trtui-dropdown-icon-item" href="#!">
                                      <img src={defaultAvatar} alt="bitbucket" />
                                      <span>{om.rowFormData.roleName}</span>
                                    </span>
                                  </div>
                                ))}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              <div className="trtui-ms-1 trtui-header-item trtui-d-flex">
                <button
                  type="button"
                  onClick={() => themeLayout(!isDark)}
                  className="trtui-btn trtui-btn-icon trtui-btn-topbar trtui-btn-ghost-secondary trtui-rounded-circle trtui-light-dark-mode"
                  data-testid="alh-dl-mode"
                >
                  <i className={`bx bx-${isDark ? 'sun' : 'moon'} trtui-fs-22`} />
                </button>
              </div>
              <div className="trtui-dropdown trtui-ms-sm-3 trtui-header-item trtui-topbar-user">
                <>
                  <button
                    type="button"
                    className={`trtui-btn ${userDropDown && 'trtui-show'}`}
                    onClick={() => setUserDropDown(!userDropDown)}
                    data-bs-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="true"
                  >
                    <span className="trtui-d-flex trtui-align-items-center">
                      <img
                        className="trtui-rounded-circle trtui-header-profile-user"
                        ref={avatarRef}
                        onError={onAvatarImageError}
                        src={
                          authDetails.authentication?.user.profile.photos[0].value || defaultAvatar
                        }
                        alt="Header Avatar"
                      />
                      <span className="trtui-text-start trtui-ms-xl-2">
                        <span className="trtui-d-none trtui-d-xl-inline-block trtui-ms-1 trtui-fw-semibold trtui-user-name-text">
                          {authDetails.session.user?.userFullName}
                        </span>
                        <span className="trtui-d-none trtui-d-xl-block trtui-ms-1 trtui-fs-12 trtui-user-name-sub-text">
                          {authDetails?.role}
                        </span>
                      </span>
                    </span>
                  </button>
                  <div
                    className={`trtui-dropdown-menu trtui-dropdown-menu-end ${
                      userDropDown && 'trtui-show'
                    }`}
                    style={{
                      position: 'absolute',
                      inset: '0px 0px auto auto',
                      margin: '0px',
                      WebkitTransform: 'translate(0px, 62px)',
                      msTransform: 'translate(0px, 62px)',
                      transform: 'translate(0px, 62px)',
                    }}
                    ref={userDropDownRef}
                  >
                    <h6 className="trtui-dropdown-header">
                      Welcome {authDetails?.session?.user.userFullName}!
                    </h6>
                    {!modSchema.noLayout && envData.REACT_APP_TYPE === 'app' && (
                      <>
                        <Link className="trtui-dropdown-item" to="/console/my-profile">
                          <i className="mdi mdi-account-circle trtui-text-muted trtui-fs-16 trtui-align-middle trtui-me-1" />
                          <span className="trtui-align-middle">Profile</span>
                        </Link>
                        <Link className="trtui-dropdown-item" to="/console/my-messages">
                          <i className="mdi mdi-message-text-outline trtui-text-muted trtui-fs-16 trtui-align-middle trtui-me-1" />
                          <span className="trtui-align-middle">Messages</span>
                        </Link>
                      </>
                    )}
                    {!modSchema.noLayout && (
                      <Link className="trtui-dropdown-item" to="help">
                        <i className="mdi mdi-lifebuoy trtui-text-muted trtui-fs-16 trtui-align-middle trtui-me-1" />
                        <span className="trtui-align-middle">Help</span>
                      </Link>
                    )}
                    <div className="trtui-dropdown-divider" />
                    <Link className="trtui-dropdown-item" to="/console/logout">
                      <i className="mdi mdi-logout trtui-text-muted trtui-fs-16 trtui-align-middle trtui-me-1" />
                      <span className="trtui-align-middle" data-key="t-logout">
                        Logout
                      </span>
                    </Link>
                  </div>
                </>
              </div>
            </div>
          </div>
        </div>
      </header>
      {installable && (
        <div className="trtui-customizer-setting trtui-d-none trtui-d-md-block">
          <div
            className="trtui-btn-info trtui-rounded-pill trtui-shadow-lg trtui-btn trtui-btn-icon trtui-btn-lg p-2"
            data-bs-toggle="offcanvas"
            data-bs-target="#theme-settings-offcanvas"
            aria-controls="theme-settings-offcanvas"
            onClick={handleInstallClick}
            data-testid="alh-installable-button"
          >
            <i className="mdi mdi-open-in-app trtui-fs-22"></i>
          </div>
        </div>
      )}
    </>
  )
}

AppLayoutHeader.propTypes = {
  envData: PropTypes.object.isRequired,
  authDetails: PropTypes.object.isRequired,
  setToggleMenu: PropTypes.func.isRequired,
  toggleMenu: PropTypes.bool.isRequired,
  modSchema: PropTypes.object,
  activeOrg: PropTypes.string,
  unsetOrg: PropTypes.func,
  activeOrgRoutes: PropTypes.object,
  isDefaultOrg: PropTypes.object,
  setIsDefaultOrg: PropTypes.func,
  removeDefaultApp: PropTypes.func,
  setApp: PropTypes.func,
}
